// src/utils/apiClient.js
//
// Central Axios client (single refresh owner)
// ===========================================
//
// THIS FILE IS THE ONLY PLACE THAT OWNS:
// - attaching identity headers (Device-Id, Browser-Id)
// - attaching X-Session-Id selector when available
// - refresh-on-401 logic (single-flight + retry queue)
//
// CURRENT ARCHITECTURE
// --------------------
// Backend model:
// - protected routes are token-first (JWT with sid)
// - refresh/logout are cookie-first
// - strict tab-selected auth is enforced server-side
//
// Frontend model:
// - cookies are shared across tabs
// - sessionStorage is tab-local
// - X-Session-Id is a TAB-LOCAL selector
//
// IMPORTANT SECURITY DECISION
// ---------------------------
// This file is aligned with STRICT TAB-SELECTED AUTH.
//
// That means:
// - no registry rehydrate here
// - no duplicate-tab recovery here
// - no session discovery here
// - no attempt to "guess" another session for this tab
//
// If THIS tab has no valid local session selector and backend requires one,
// this tab must fail closed and return user to normal login flow.
//
// IMPORTANT SAFETY RULE
// ---------------------
// NEVER call /auth/logout automatically on refresh failure.
// That would clear cookies and unintentionally log out other tabs/pages
// of the same shared user session.
//
// Instead:
// - clear local auth only in THIS tab
// - clear THIS tab's session selector
// - surface a typed local error to the UI
//
// DIVISION OF RESPONSIBILITY
// --------------------------
// apiClient owns transport/auth refresh mechanics.
// It does NOT own page redirects and does NOT own UI logout decisions.
//
// AuthContext reacts to token updates published by this module.
//

import axios from "axios";
import { getDeviceId, getBrowserId } from "./clientIdentity";
import * as clientIdentity from "./clientIdentity";
import {
  getSessionId,
  getCurrentSessionSnapshot,
  clearTabSessionIdentity,
  setSessionIdentity,
  hasTabSessionIdentity,
} from "./sessionIdentity";

const DEV = process.env.NODE_ENV !== "production";

/**
 * Browser-local event emitted when ordinary REST traffic proves that this tab
 * is now talking to a different backend process runtime.
 *
 * This is intentionally emitted from apiClient because apiClient already sees
 * every relevant HTTP response.  We do not add any new REST call and we do not
 * import socketClient here, which keeps the dependency direction clean.
 */
export const BACKEND_RUNTIME_CHANGED_EVENT = "hb:backend-runtime-changed";

let hasSeenBackendRuntimeId = false;
let lastSeenBackendRuntimeId = "";

/* -------------------------------------------------------------------------- */
/* Debug logging helpers                                                      */
/* -------------------------------------------------------------------------- */

function dlog(label, payload) {
  if (!DEV) return;
  // eslint-disable-next-line no-console
  console.log(`[apiClient] ${label}`, payload || "");
}

function dwarn(label, payload) {
  if (!DEV) return;
  // eslint-disable-next-line no-console
  console.warn(`[apiClient] ${label}`, payload || "");
}

function derr(label, payload) {
  if (!DEV) return;
  // eslint-disable-next-line no-console
  console.error(`[apiClient] ${label}`, payload || "");
}

function shortId(value) {
  const s = String(value || "").trim();
  if (!s) return "";
  if (s.length <= 12) return s;
  return `${s.slice(0, 6)}…${s.slice(-4)}`;
}

function normalizeString(value) {
  const s = String(value || "").trim();
  return s || "";
}

/* -------------------------------------------------------------------------- */
/* Base URL                                                                   */
/* -------------------------------------------------------------------------- */

const apiBase =
  process.env.REACT_APP_API_URL ||
  (process.env.NODE_ENV !== "production" ? "http://localhost:3001" : "");

if (!apiBase && !DEV) {
  // eslint-disable-next-line no-console
  console.error("[apiClient] Missing REACT_APP_API_URL in production.");
}

/* -------------------------------------------------------------------------- */
/* Axios instances                                                            */
/* -------------------------------------------------------------------------- */

/**
 * Main client for normal application traffic.
 *
 * This instance participates in:
 * - request header injection
 * - automatic refresh flow
 * - token propagation to AuthContext
 */
const apiClient = axios.create({
  baseURL: apiBase,
  withCredentials: true,
});

/**
 * Raw client used ONLY for refresh.
 *
 * Why separate instance?
 * - avoids recursive interceptor loops
 * - keeps refresh logic easier to reason about
 * - prevents "refresh request itself triggers refresh again" bugs
 */
const rawClient = axios.create({
  baseURL: apiBase,
  withCredentials: true,
});

/* -------------------------------------------------------------------------- */
/* Token propagation hook                                                     */
/* -------------------------------------------------------------------------- */

/**
 * AuthContext installs a callback here.
 *
 * apiClient never updates React state directly.
 * Instead it publishes token changes and lets AuthContext decide how to react.
 */
let onTokenUpdate = null;

export function setOnTokenUpdate(fn) {
  onTokenUpdate = typeof fn === "function" ? fn : null;

  dlog("[TOKEN] setOnTokenUpdate", {
    installed: Boolean(onTokenUpdate),
  });
}

function notifyTokenUpdate(tokenOrNull) {
  try {
    dlog("[TOKEN] notifyTokenUpdate", {
      hasToken: Boolean(tokenOrNull),
      tokenPreview: tokenOrNull ? `${String(tokenOrNull).slice(0, 16)}…` : null,
    });

    if (onTokenUpdate) {
      onTokenUpdate(tokenOrNull || null);
    }
  } catch (e) {
    dwarn("[TOKEN] notifyTokenUpdate callback failed", {
      message: e?.message,
    });
  }
}

/* -------------------------------------------------------------------------- */
/* Authorization header helper                                                */
/* -------------------------------------------------------------------------- */

/**
 * Set or clear the default Authorization header on the main client.
 *
 * IMPORTANT:
 * The access token is held only in runtime memory here.
 * It is intentionally NOT persisted to localStorage/sessionStorage.
 *
 * That keeps the model safer and forces each tab to earn its own authenticated
 * runtime through explicit login or successful strict bootstrap.
 */
export function setAuthToken(accessToken) {
  if (accessToken && typeof accessToken === "string") {
    apiClient.defaults.headers.common.Authorization = `Bearer ${accessToken}`;

    dlog("[TOKEN] setAuthToken", {
      action: "set",
      preview: `${accessToken.slice(0, 16)}…`,
    });
  } else {
    delete apiClient.defaults.headers.common.Authorization;

    dlog("[TOKEN] setAuthToken", {
      action: "clear",
    });
  }
}

/* -------------------------------------------------------------------------- */
/* Dev-safe logging                                                           */
/* -------------------------------------------------------------------------- */

function safeAxiosError(err) {
  return {
    message: err?.message,
    status: err?.response?.status,
    url: err?.config?.url,
    method: err?.config?.method,
  };
}

/* -------------------------------------------------------------------------- */
/* Browser-Id resync support                                                  */
/* -------------------------------------------------------------------------- */

/**
 * Backend may tell frontend to resync Browser-Id using X-Browser-Id.
 *
 * Why this exists:
 * - backend cookie may become canonical for the current host
 * - frontend local browser identity should follow that canonical value
 */
function applyServerBrowserId(serverBrowserId) {
  const v = String(serverBrowserId || "").trim();
  if (!v) return;

  dlog("[BROWSER] applyServerBrowserId", {
    browserId: v,
  });

  try {
    if (typeof clientIdentity.setBrowserId === "function") {
      clientIdentity.setBrowserId(v);
      return;
    }
  } catch {
    // ignore
  }

  try {
    localStorage.setItem("browserId", v);
  } catch {
    // ignore
  }
}

function readServerBrowserIdFromHeaders(headers) {
  if (!headers) return null;
  return headers["x-browser-id"] || headers["x-browserid"] || null;
}

function readSessionRequiredHint(headers) {
  if (!headers) return false;
  const v = headers["x-session-required"] || null;
  return String(v || "").toLowerCase() === "true";
}

/**
 * Read the backend runtime fingerprint from response headers.
 *
 * Axios normalizes header names to lowercase in browsers, but the fallback
 * mixed-case checks make the helper safer for tests and non-browser adapters.
 */
function readBackendRuntimeHintFromHeaders(headers) {
  if (!headers) return null;

  const runtimeId = normalizeString(
    headers["x-hb-backend-runtime-id"] ||
      headers["X-HB-Backend-Runtime-Id"] ||
      ""
  );

  if (!runtimeId) return null;

  return {
    runtimeId,
    startedAt:
      normalizeString(
        headers["x-hb-backend-started-at"] ||
          headers["X-HB-Backend-Started-At"] ||
          ""
      ) || null,
    processId:
      normalizeString(
        headers["x-hb-backend-process-id"] ||
          headers["X-HB-Backend-Process-Id"] ||
          ""
      ) || null,
  };
}

/**
 * Process a backend runtime fingerprint.
 *
 * First observed runtime is only remembered.  A change means that this browser
 * tab survived a backend process restart.  The frontend may then decide to
 * recreate its Socket.IO connection, because backend in-memory socket rooms were
 * reset by the restart.
 */
function processBackendRuntimeHint(headers, context = {}) {
  const hint = readBackendRuntimeHintFromHeaders(headers);
  if (!hint?.runtimeId) return;

  if (!hasSeenBackendRuntimeId) {
    hasSeenBackendRuntimeId = true;
    lastSeenBackendRuntimeId = hint.runtimeId;

    dlog("[BACKEND_RUNTIME] initial runtime observed", {
      ...hint,
      context,
    });
    return;
  }

  if (hint.runtimeId === lastSeenBackendRuntimeId) {
    return;
  }

  const previousRuntimeId = lastSeenBackendRuntimeId;
  lastSeenBackendRuntimeId = hint.runtimeId;

  const detail = {
    ...hint,
    previousRuntimeId,
    detectedAt: new Date().toISOString(),
    context,
  };

  dlog("[BACKEND_RUNTIME] backend runtime changed", detail);

  if (typeof window !== "undefined") {
    try {
      window.dispatchEvent(
        new CustomEvent(BACKEND_RUNTIME_CHANGED_EVENT, {
          detail,
        })
      );
    } catch (err) {
      dwarn("[BACKEND_RUNTIME] event dispatch failed", {
        message: err?.message || String(err || ""),
      });
    }
  }
}

/* -------------------------------------------------------------------------- */
/* URL helpers                                                                */
/* -------------------------------------------------------------------------- */

function getUrlFromConfig(config) {
  return String(config?.url || "");
}

function isAuthEndpoint(url) {
  const u = String(url || "");
  return (
    u.includes("/auth/login") ||
    u.includes("/auth/register") ||
    u.includes("/auth/logout") ||
    u.includes("/auth/refresh")
  );
}

function isCheckAuthEndpoint(url) {
  return String(url || "").includes("/auth/check-auth");
}

/* -------------------------------------------------------------------------- */
/* Session selector helpers                                                   */
/* -------------------------------------------------------------------------- */

/**
 * Return current tab-local session selector only.
 *
 * IMPORTANT:
 * In strict tab-selected auth we do NOT attempt any fallback here.
 *
 * If the current tab has no explicit session selector, we send no X-Session-Id.
 * The backend is then free to fail closed.
 */
function getSessionIdForRequest() {
  const sid = getSessionId() || "";

  dlog("[SESSION] getSessionIdForRequest", {
    sessionId: sid || null,
    snapshot: getCurrentSessionSnapshot(),
  });

  return sid;
}

/**
 * Read current tab-local session selector without fallback.
 *
 * Mainly useful for:
 * - diagnostics
 * - strict retry decisions
 */
function peekTabSessionId() {
  const sid = getCurrentSessionSnapshot().sessionId || "";

  dlog("[SESSION] peekTabSessionId", {
    sessionId: sid || null,
  });

  return sid;
}

/**
 * Strict check:
 * does THIS tab currently own local session authority?
 *
 * We intentionally require both:
 * - a non-empty local session id
 * - sessionIdentity confirming local ownership
 *
 * This keeps request/refresh logic aligned with AuthContext bootstrap rules.
 */
function hasStrictLocalSessionAuthority() {
  const sid = normalizeString(getSessionIdForRequest());
  return Boolean(sid && hasTabSessionIdentity());
}

/**
 * Build request identity headers.
 *
 * Device-Id and Browser-Id are always sent.
 * X-Session-Id is sent only when THIS tab explicitly owns it.
 */
function buildIdentityHeaders({ includeSessionId = true } = {}) {
  const sessionId = includeSessionId ? getSessionIdForRequest() : "";

  const headers = {
    "Device-Id": getDeviceId(),
    "Browser-Id": getBrowserId(),
  };

  if (sessionId) {
    headers["X-Session-Id"] = sessionId;
  }

  dlog("[REQUEST] buildIdentityHeaders", {
    includeSessionId,
    sessionId: sessionId || null,
    deviceId: headers["Device-Id"],
    browserId: headers["Browser-Id"],
  });

  return headers;
}

/* -------------------------------------------------------------------------- */
/* JWT payload decode helper                                                  */
/* -------------------------------------------------------------------------- */

/**
 * Decode JWT payload without verification.
 *
 * IMPORTANT:
 * - used only for convenience after backend already issued a token
 * - never used as the sole security decision source
 */
function decodeJwtPayload(token) {
  try {
    const parts = String(token || "").split(".");
    if (parts.length < 2) return null;

    const base64Url = parts[1];
    const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
    const padded = base64.padEnd(Math.ceil(base64.length / 4) * 4, "=");

    const json =
      typeof atob === "function"
        ? atob(padded)
        : Buffer.from(padded, "base64").toString("utf8");

    return JSON.parse(json);
  } catch {
    return null;
  }
}

/* -------------------------------------------------------------------------- */
/* Local auth collapse                                                        */
/* -------------------------------------------------------------------------- */

/**
 * Clear local auth state for THIS TAB only.
 *
 * This helper is intentionally low-level and transport-focused.
 * It does not redirect and does not call backend logout.
 */
function clearLocalAuthState() {
  dwarn("[AUTH] clearLocalAuthState", {
    snapshotBefore: getCurrentSessionSnapshot(),
  });

  setAuthToken(null);
  notifyTokenUpdate(null);
  clearTabSessionIdentity();

  dlog("[AUTH] clearLocalAuthState done", {
    snapshotAfter: getCurrentSessionSnapshot(),
  });
}

/* -------------------------------------------------------------------------- */
/* Request interceptor                                                        */
/* -------------------------------------------------------------------------- */

apiClient.interceptors.request.use(
  (config) => {
    config.headers = config.headers || {};
    config.withCredentials = true;

    const url = getUrlFromConfig(config);
    const identityHeaders = buildIdentityHeaders({ includeSessionId: true });

    Object.assign(config.headers, identityHeaders);

    /**
     * STRICT TAB-SELECTED HARDENING
     * -----------------------------
     * /auth/check-auth must represent THIS TAB only.
     *
     * Therefore if THIS tab does not own local session authority,
     * we proactively strip Authorization before the request leaves.
     *
     * Why?
     * - prevents accidental "half-authenticated" bootstrap when a stale runtime
     *   token somehow exists without valid current-tab session authority
     * - keeps check-auth semantics aligned with AuthContext strict bootstrap
     *
     * Important:
     * this is intentionally narrow to /auth/check-auth only.
     * We do not globally strip Authorization from all requests here.
     */
    if (isCheckAuthEndpoint(url) && !hasStrictLocalSessionAuthority()) {
      delete config.headers.Authorization;
      delete config.headers.authorization;

      dlog("[REQUEST] stripped Authorization from /auth/check-auth because tab lacks local session authority", {
        url,
        tabSessionId: shortId(getCurrentSessionSnapshot().sessionId),
      });
    }

    dlog("[REQUEST] outgoing", {
      method: config.method,
      url,
      hasAuthorization:
        Boolean(config.headers?.Authorization) ||
        Boolean(config.headers?.authorization),
      sessionId: config.headers?.["X-Session-Id"] || null,
      snapshot: getCurrentSessionSnapshot(),
    });

    return config;
  },
  (error) => {
    derr("[REQUEST] interceptor error", safeAxiosError(error));
    return Promise.reject(error);
  }
);

/* -------------------------------------------------------------------------- */
/* Refresh machinery                                                          */
/* -------------------------------------------------------------------------- */

let isRefreshing = false;
let failedRequestsQueue = [];

/**
 * Flush the refresh wait queue.
 *
 * Every queued request either:
 * - receives the new token and continues
 * - or receives the refresh error and fails
 */
function flushQueue(error, newToken) {
  const q = failedRequestsQueue;
  failedRequestsQueue = [];

  dlog("[REFRESH] flushQueue", {
    queuedCount: q.length,
    hasError: Boolean(error),
    hasNewToken: Boolean(newToken),
  });

  q.forEach(({ resolve, reject }) => {
    if (error) reject(error);
    else resolve(newToken);
  });
}

/**
 * Attempt refresh for the CURRENT TAB only.
 *
 * IMPORTANT:
 * In strict tab-selected auth, refresh is meaningful only when THIS TAB already
 * has its own session selector.
 *
 * We still allow backend to decide final validity, but we do not attempt to
 * manufacture a session context for this tab.
 */
async function tryRefreshAccessToken() {
  const snapshotBefore = getCurrentSessionSnapshot();
  const existingSid = getSessionIdForRequest();

  dlog("[REFRESH] tryRefreshAccessToken:start", {
    snapshotBefore,
    existingSid: existingSid || null,
  });

  /**
   * STRICT MODE:
   * no local session authority => do not even attempt refresh
   *
   * This is important because refresh is cookie-first on the backend,
   * but still must remain current-tab-selected in your architecture.
   */
  if (!existingSid || !hasTabSessionIdentity()) {
    dwarn("[REFRESH] skipped because this tab has no strict local session authority", {
      snapshotBefore,
    });
    return null;
  }

  const res = await rawClient.post("/auth/refresh", null, {
    headers: buildIdentityHeaders({
      includeSessionId: true,
    }),
    withCredentials: true,
    validateStatus: () => true,
  });

  dlog("[REFRESH] raw response", {
    status: res?.status,
    hasAccessToken: Boolean(res?.data?.accessToken),
    responseSessionId: res?.data?.sessionId || null,
  });

  processBackendRuntimeHint(res?.headers, {
    source: "raw-refresh",
    status: res?.status || null,
    url: "/auth/refresh",
  });

  const browserHdr = readServerBrowserIdFromHeaders(res?.headers);
  if (browserHdr) {
    applyServerBrowserId(browserHdr);
  }

  if (res?.status !== 200 || !res?.data?.accessToken) {
    dwarn("[REFRESH] refresh rejected / failed", {
      status: res?.status,
      data: res?.data,
    });
    return null;
  }

  const accessToken = String(res.data.accessToken || "").trim();
  const payload = decodeJwtPayload(accessToken) || {};

  /**
   * Preferred order:
   * 1) backend explicit sessionId
   * 2) JWT sid
   * 3) already known tab-local sessionId
   *
   * No recovery and no shared fallback.
   */
  const sessionId =
    String(res.data.sessionId || payload.sid || existingSid || "").trim() || "";

  const userId =
    payload?.id != null
      ? String(payload.id)
      : String(snapshotBefore.userId || "");

  if (sessionId) {
    setSessionIdentity({
      sessionId,
      userId,
    });
  }

  dlog("[REFRESH] success", {
    sessionId: sessionId || null,
    userId: userId || null,
    payloadSid: payload?.sid || null,
    payloadUserId: payload?.id || null,
  });

  return {
    accessToken,
    sessionId,
    userId,
  };
}

/* -------------------------------------------------------------------------- */
/* Session-required handling                                                  */
/* -------------------------------------------------------------------------- */

/**
 * In strict tab-selected auth we allow ONLY one narrowly scoped retry:
 * - retry with the CURRENT tab-local X-Session-Id if it exists
 *
 * We do NOT:
 * - rehydrate from registry
 * - discover from other tabs
 * - infer from duplicate/source relationships
 *
 * So the retry is not a recovery attempt.
 * It is only a replay using THIS TAB's already-owned selector.
 */
function getRetryableLocalSessionId() {
  const currentTabSid = peekTabSessionId();

  if (currentTabSid) {
    dlog("[SESSION] getRetryableLocalSessionId -> current tab sid", {
      sessionId: currentTabSid,
    });
    return currentTabSid;
  }

  dwarn("[SESSION] getRetryableLocalSessionId -> none", {
    snapshot: getCurrentSessionSnapshot(),
  });

  return "";
}

/* -------------------------------------------------------------------------- */
/* Response interceptor                                                       */
/* -------------------------------------------------------------------------- */

apiClient.interceptors.response.use(
  (response) => {
    processBackendRuntimeHint(response?.headers, {
      source: "api-response-success",
      status: response?.status || null,
      url: response?.config?.url || null,
      method: response?.config?.method || null,
    });

    const hdr = readServerBrowserIdFromHeaders(response?.headers);
    if (hdr) applyServerBrowserId(hdr);

    dlog("[RESPONSE] success", {
      status: response?.status,
      url: response?.config?.url,
      method: response?.config?.method,
      responseSessionId: response?.data?.sessionId || null,
      snapshot: getCurrentSessionSnapshot(),
    });

    return response;
  },
  async (error) => {
    processBackendRuntimeHint(error?.response?.headers, {
      source: "api-response-error",
      status: error?.response?.status || null,
      url: error?.config?.url || null,
      method: error?.config?.method || null,
    });

    const hdr = readServerBrowserIdFromHeaders(error?.response?.headers);
    if (hdr) applyServerBrowserId(hdr);

    const status = error?.response?.status;
    const originalRequest = error?.config || {};
    const url = String(originalRequest?.url || "");

    derr("[RESPONSE] error", {
      ...safeAxiosError(error),
      sessionRequired: readSessionRequiredHint(error?.response?.headers),
      snapshot: getCurrentSessionSnapshot(),
      originalRetry: Boolean(originalRequest?._retry),
      retriedAfterSessionRecovery: Boolean(originalRequest?._retriedAfterSessionRecovery),
    });

    /**
     * 403 is not a refresh concern.
     * It usually means:
     * - forbidden business action
     * - inactive/unapproved account
     * - role violation
     *
     * So we surface it directly.
     */
    if (status === 403) {
      return Promise.reject(error);
    }

    const sessionRequired = readSessionRequiredHint(error?.response?.headers);

    /**
     * Backend says this request requires an explicit X-Session-Id.
     *
     * Strict tab-selected behavior:
     * - use current local tab selector if available
     * - otherwise fail closed in THIS tab
     */
    if (status === 401 && sessionRequired) {
      dwarn("[401] SESSION_ID_REQUIRED branch entered", {
        url,
        snapshot: getCurrentSessionSnapshot(),
      });

      if (!originalRequest._retriedAfterSessionRecovery) {
        const localSid = getRetryableLocalSessionId();

        if (localSid) {
          originalRequest._retriedAfterSessionRecovery = true;
          originalRequest.headers = originalRequest.headers || {};
          originalRequest.headers["X-Session-Id"] = localSid;

          dwarn("[401] retry with current local tab session id", {
            url,
            localSid,
          });

          return apiClient(originalRequest);
        }
      }

      const e = new Error(
        "This tab must provide a valid X-Session-Id for the current session context."
      );
      e.code = "SESSION_ID_REQUIRED";
      e.status = 401;

      dwarn("[401] SESSION_ID_REQUIRED final failure", {
        url,
        snapshot: getCurrentSessionSnapshot(),
      });

      clearLocalAuthState();
      return Promise.reject(e);
    }

    const hadAuthHeader =
      Boolean(originalRequest?.headers?.Authorization) ||
      Boolean(originalRequest?.headers?.authorization);

    /**
     * Refresh should happen only for:
     * - normal protected API calls that already had Authorization
     * - /auth/check-auth bootstrap path
     *
     * It should not happen for:
     * - auth endpoints
     * - already retried requests
     * - random unauthenticated traffic
     */
    const refreshAllowed = isCheckAuthEndpoint(url) || hadAuthHeader;

    const skipRefresh =
      status !== 401 ||
      isAuthEndpoint(url) ||
      !refreshAllowed ||
      originalRequest._retry;

    if (skipRefresh) {
      dlog("[401] skipRefresh", {
        url,
        status,
        isAuthEndpoint: isAuthEndpoint(url),
        refreshAllowed,
        alreadyRetried: Boolean(originalRequest._retry),
      });
      return Promise.reject(error);
    }

    /**
     * STRICT MODE HARDENING:
     * Do not refresh for /auth/check-auth when the current tab does not own
     * local session authority.
     *
     * This is critical for preventing "ghost" or half-authenticated duplicate tabs.
     */
    if (isCheckAuthEndpoint(url) && !hasStrictLocalSessionAuthority()) {
      dwarn("[401] refusing refresh for /auth/check-auth because tab lacks strict local session authority", {
        url,
        snapshot: getCurrentSessionSnapshot(),
      });

      clearLocalAuthState();

      const e = new Error(
        "This tab does not own a local session selector and cannot bootstrap auth."
      );
      e.code = "STRICT_TAB_AUTH_REQUIRED";
      e.status = 401;

      return Promise.reject(e);
    }

    originalRequest._retry = true;

    if (isRefreshing) {
      dlog("[REFRESH] queue request during active refresh", {
        url,
        queueLengthBefore: failedRequestsQueue.length,
      });

      return new Promise((resolve, reject) => {
        failedRequestsQueue.push({ resolve, reject });
      })
        .then((newToken) => {
          originalRequest.headers = originalRequest.headers || {};
          originalRequest.headers.Authorization = `Bearer ${newToken}`;

          dlog("[REFRESH] replay queued request", {
            url,
          });

          return apiClient(originalRequest);
        })
        .catch((err) => Promise.reject(err));
    }

    isRefreshing = true;

    try {
      dlog("[REFRESH] start single-flight", {
        url,
        snapshot: getCurrentSessionSnapshot(),
      });

      const refreshResult = await tryRefreshAccessToken();

      if (!refreshResult?.accessToken) {
        const refreshErr = new Error(
          "Refresh failed (cookie missing / expired / invalid)."
        );
        refreshErr.code = "REFRESH_FAILED";
        refreshErr.status = 401;

        dwarn("[REFRESH] failed - no access token returned", {
          url,
          snapshot: getCurrentSessionSnapshot(),
        });

        flushQueue(refreshErr, null);
        clearLocalAuthState();

        return Promise.reject(refreshErr);
      }

      const newToken = refreshResult.accessToken;

      setAuthToken(newToken);
      notifyTokenUpdate(newToken);

      flushQueue(null, newToken);

      originalRequest.headers = originalRequest.headers || {};
      originalRequest.headers.Authorization = `Bearer ${newToken}`;

      dlog("[REFRESH] replay original request after refresh", {
        url,
        sessionId: refreshResult.sessionId || null,
        userId: refreshResult.userId || null,
      });

      return apiClient(originalRequest);
    } catch (refreshError) {
      derr("[REFRESH] path failed", {
        ...safeAxiosError(refreshError),
        snapshot: getCurrentSessionSnapshot(),
      });

      flushQueue(refreshError, null);
      clearLocalAuthState();
      return Promise.reject(refreshError);
    } finally {
      isRefreshing = false;

      dlog("[REFRESH] end single-flight", {
        queueLengthAfter: failedRequestsQueue.length,
      });
    }
  }
);

export default apiClient;

